import { OnlineServiceFeeRowFrame, type OnlineServiceFeeRowProps } from '@order-up/common';
import { createClientLogger } from '@order-up/client/logger';
import { captureException } from '@sentry/react';
import { Component, lazy, Suspense, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

const OnlineServiceFeeRow = lazy(() => import('./onlineServiceFeeRowChunk'));
const logger = createClientLogger('LazyOnlineServiceFeeRow');

/**
 * Checkout's Online Service Fee row. The row itself (label, ⓘ, screen-reader
 * explanation, amount) renders at once from the shared frame; the ⓘ's popover
 * arrives in its own chunk, fetched when the row first renders.
 *
 * - A tap on the ⓘ before that chunk lands is remembered and the popover opens
 *   as soon as it does.
 * - Keyboard focus on the placeholder ⓘ moves to the loaded one at the swap.
 * - A chunk that never arrives (offline blip, a deploy that replaced its hash)
 *   must not reach the root error boundary — that would take down checkout or
 *   a PAID order's confirmation for a tooltip. It is reported, and the row stays
 *   with the ⓘ disclosing the explanation inline (no popover machinery needed).
 */
export default function LazyOnlineServiceFeeRow(props: OnlineServiceFeeRowProps) {
    const [openOnLoad, setOpenOnLoad] = useState(false);
    const boxRef = useRef<HTMLDivElement>(null);
    // True while the placeholder ⓘ holds focus. Removing a focused element does
    // not reliably fire blur, so a blur clears this only if the placeholder is
    // still in the document afterwards (a real move elsewhere).
    const placeholderFocusedRef = useRef(false);
    return (
        <div
            ref={boxRef}
            className="contents"
            onFocusCapture={(e) => {
                if (e.target instanceof HTMLElement && e.target.matches('[data-fee-info-trigger]:not([data-state])')) {
                    placeholderFocusedRef.current = true;
                }
            }}
            onBlurCapture={(e) => {
                const el = e.target;
                setTimeout(() => {
                    if (el.isConnected) placeholderFocusedRef.current = false;
                }, 0);
            }}
        >
            <FeeRowLoadBoundary fallback={<InlineExplanationRow {...props} />}>
                <Suspense fallback={<OnlineServiceFeeRowFrame {...props} onInfoActivate={() => setOpenOnLoad(true)} />}>
                    <FocusHandoff boxRef={boxRef} placeholderFocusedRef={placeholderFocusedRef} />
                    <OnlineServiceFeeRow {...props} defaultOpen={openOnLoad} />
                </Suspense>
            </FeeRowLoadBoundary>
        </div>
    );
}

/** Mounts with the loaded row; moves focus to its ⓘ if the swapped-out placeholder had it. */
function FocusHandoff({ boxRef, placeholderFocusedRef }: { boxRef: RefObject<HTMLDivElement | null>; placeholderFocusedRef: RefObject<boolean> }) {
    useLayoutEffect(() => {
        if (!placeholderFocusedRef.current) return;
        placeholderFocusedRef.current = false;
        const active = document.activeElement;
        if (active && active !== document.body) return; // focus already went somewhere on purpose
        boxRef.current?.querySelector<HTMLElement>('[data-fee-info-trigger]')?.focus();
    }, [boxRef, placeholderFocusedRef]);
    return null;
}

/** The row without a popover: the ⓘ toggles the explanation inline. */
function InlineExplanationRow(props: OnlineServiceFeeRowProps) {
    const [open, setOpen] = useState(false);
    return <OnlineServiceFeeRowFrame {...props} explanationVisible={open} onInfoActivate={() => setOpen((o) => !o)} />;
}

class FeeRowLoadBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error: unknown) {
        logger.error('Online Service Fee popover chunk failed to load; showing the inline explanation', error);
        captureException(error, { tags: { component: 'LazyOnlineServiceFeeRow' } });
    }

    render() {
        return this.state.failed ? this.props.fallback : this.props.children;
    }
}
